// Account administration (/app/v1/admin, ADR 0007, temporary): the rules both sides share.
import { z } from "zod";
import { AccountType } from "./identity.js";

/** Which account types each type may create, change and remove (ADR 0007). */
export const manageableTypes: Readonly<Record<AccountType, readonly AccountType[]>> = {
  "tenant-admin": ["tenant-admin", "admin", "user"],
  admin: ["user"],
  user: [],
};

/** Whether `caller` may act on every one of `types`. */
export const manages = (caller: AccountType, ...types: AccountType[]): boolean =>
  types.every((type) => manageableTypes[caller].includes(type));

export const AccountStatus = z.enum(["invited", "active", "disabled"]);
export type AccountStatus = z.infer<typeof AccountStatus>;

export const Account = z.object({
  id: z.string().min(1),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  type: AccountType,
  status: AccountStatus,
  createdAt: z.string().datetime(),
});
export type Account = z.infer<typeof Account>;

export const Accounts = z.array(Account);

const Email = z.string().trim().toLowerCase().pipe(z.email().max(254));

/** One type for up to 20 addresses; the person enters their names at first sign-in. */
export const InviteRequest = z.object({
  emails: z
    .array(Email)
    .min(1)
    .max(20)
    .refine((list) => new Set(list).size === list.length, "duplicate address"),
  type: AccountType,
});
export type InviteRequest = z.infer<typeof InviteRequest>;

export const InviteOutcome = z.object({
  email: z.string(),
  outcome: z.enum(["invited", "exists", "failed"]),
  account: Account.optional(),
});
export type InviteOutcome = z.infer<typeof InviteOutcome>;

export const InviteResult = z.object({ results: z.array(InviteOutcome) });
export type InviteResult = z.infer<typeof InviteResult>;

const Name = z.string().trim().min(1).max(100);

/** A change of type, of enabled, of names, or several; never empty. */
export const AccountPatch = z
  .object({
    type: AccountType.optional(),
    enabled: z.boolean().optional(),
    firstName: Name.optional(),
    lastName: Name.optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), "nothing to change");
export type AccountPatch = z.infer<typeof AccountPatch>;

/** Members of an organization by account type and by status. */
export const MemberCounts = z.object({
  "tenant-admin": z.number().int(),
  admin: z.number().int(),
  user: z.number().int(),
  invited: z.number().int(),
  disabled: z.number().int(),
});
export type MemberCounts = z.infer<typeof MemberCounts>;

/** The caller's organization (/app/v1/admin/organization): only `name` changes. */
export const Organization = z.object({
  id: z.string(),
  alias: z.string(),
  name: z.string(),
  domains: z.array(z.string()),
  counts: MemberCounts,
});
export type Organization = z.infer<typeof Organization>;

export const OrganizationPatch = z.object({ name: z.string().trim().min(1).max(100) });
export type OrganizationPatch = z.infer<typeof OrganizationPatch>;

const urn = <N extends string>(name: N) => `urn:cosmosai:web:problem:${name}` as const;

/** Problem types of the admin routes, with their HTTP status in a comment. */
export const adminProblemTypes = {
  adminNotConfigured: urn("admin-not-configured"), // 503
  accountTypeNotAllowed: urn("account-type-not-allowed"), // 403
  organizationAmbiguous: urn("organization-ambiguous"), // 409
  ownAccount: urn("own-account"), // 409
  lastSuperAdmin: urn("last-super-admin"), // 409
  notInvited: urn("not-invited"), // 409
  identityProviderUnavailable: urn("identity-provider-unavailable"), // 502
  organizationNotFound: urn("organization-not-found"), // 404
} as const;
